import { useState } from 'react';

import { Box, Button, SimpleGrid } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { ContextModalProps } from '@mantine/modals';
import useSWR from 'swr';

import { CompanyProfile } from 'models';

import { usePartnerHandlers } from 'components/company-profile/hooks/use-partner-handlers';
import { PartnerTeaser } from 'components/company-profile-edit/components/PartnerTeaser';
import { SearchBox } from 'components/search-box/SearchBox';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { CompanyProfileService } from 'services/CompanyProfileService';

const ChoosePartnerModal = ({
    innerProps,
}: ContextModalProps<{
    company: CompanyProfile;
}>) => {
    const { company: incomingCompany } = innerProps;

    const { company } = useCompanyProfile(incomingCompany);

    const [query, setQuery] = useState('');
    const [debouncedQuery] = useDebouncedValue(query, 1000);

    const { adding, handleAdd } = usePartnerHandlers({ company: incomingCompany });

    const chosenCompanies = company?.partners?.map(({ company }) => company).filter(Boolean);

    const { data, isLoading } = useSWR(
        `/searchPartnerProfiles?query=${debouncedQuery}`,
        async () =>
            CompanyProfileService.searchPartnerProfiles({
                query: debouncedQuery,
                excludeIds: debouncedQuery ? [] : chosenCompanies,
                limit: 5,
            }),
        {
            keepPreviousData: true,
        },
    );

    return (
        <>
            <Box mb="md">
                <SearchBox
                    size="lg"
                    value={query}
                    placeholder="Search for a company"
                    onChange={(event) => setQuery(event.currentTarget.value)}
                    handleReset={query ? () => setQuery('') : undefined}
                    isLoading={isLoading}
                />
            </Box>

            <SimpleGrid cols={{ base: 1, xs: 2, sm: 3 }} spacing="xs">
                {data?.docs.map((company) => (
                    <Company
                        key={company.id}
                        company={company}
                        handleAdd={handleAdd}
                        isAdding={adding === company.id}
                        isSelected={chosenCompanies?.includes(company.id)}
                    />
                ))}

                <CreatePlaceholder handleClick={() => CompanyProfileService.openCreatePartnerModal(incomingCompany)} />
            </SimpleGrid>
        </>
    );
};

const Company = ({
    company,
    handleAdd,
    isAdding,
    isSelected,
}: {
    company: CompanyProfile;
    handleAdd: (companyId: string) => void;
    isAdding?: boolean;
    isSelected?: boolean;
}) => {
    return (
        <PartnerTeaser
            partner={{ company: company.id }}
            extraButtons={
                <Button
                    fullWidth
                    size="xs"
                    variant="outline"
                    onClick={() => handleAdd(company.id)}
                    loading={isAdding}
                    disabled={isSelected}
                >
                    Add as partner
                </Button>
            }
        />
    );
};

const CreatePlaceholder = ({ handleClick }: { handleClick: () => void }) => {
    return (
        <PartnerTeaser
            partner={{ name: '+', company: '', description: 'Company not in the database?' }}
            extraButtons={
                <Button fullWidth size="xs" onClick={handleClick} mt="auto">
                    Add new company
                </Button>
            }
        />
    );
};

export { ChoosePartnerModal };
